/**
 * The Inbox: conversation list · thread · lead 360.
 *
 * This is the screen the client's team lives in, so the parts that matter are the honest ones:
 * the composer knows whether the 24-hour window is open and refuses when it is not; the thread
 * shows what shipped with its delivery state; the 360 shows each fact with the source that wrote
 * it and each scoring factor with the customer's own words that earned the points.
 *
 * Filters live in the URL so a view can be sent to a colleague.
 *
 * Author: Danish Khan
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { api } from '../lib/api.ts';
import {
  customerTypeLabel, formatAgo, formatDateTime, formatDuration, formatTime, humanize, LANG_LABEL,
  personLabel, stageLabel,
} from '../lib/format.ts';
import { canSeeExplain, canSteer, canWrite, phoneFor, roleGate } from '../lib/pii.ts';
import { useTenant, useUser } from '../lib/session.tsx';
import type { Conversation, InboxView, Lang, Lead, Tier } from '../lib/types.ts';
import { Bubble } from '../components/Bubble.tsx';
import { Button } from '../components/Button.tsx';
import { Drawer } from '../components/Drawer.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { ErrorState } from '../components/ErrorState.tsx';
import { ExplainPanel } from '../components/ExplainPanel.tsx';
import { Pill, TierPill } from '../components/Pill.tsx';
import { Timeline } from '../components/Timeline.tsx';
import { useToast } from '../components/Toast.tsx';

const VIEWS: { key: InboxView; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'unanswered', label: 'Unanswered' },
  { key: 'handed_over', label: 'Handed over' },
  { key: 'mine', label: 'Mine' },
];

export function InboxPage(): ReactNode {
  const user = useUser();
  // The pack names the agent; the session carries it. "Maya" was written into the JSX, which is
  // right for this tenant and wrong for the next one.
  const { agentName } = useTenant();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const [params, setParams] = useSearchParams();
  const { toast, toastError } = useToast();
  const queryClient = useQueryClient();

  const view = (params.get('view') ?? 'all') as InboxView;
  const tier = (params.get('tier') ?? 'all') as Tier | 'all';
  const lang = (params.get('lang') ?? 'all') as Lang | 'all';
  const q = params.get('q') ?? '';

  const setParam = (key: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (!value || value === 'all') next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  /**
   * Fifty conversations at a time, keyset on (activity instant, id) — what `GET /inbox` already
   * pages by. Until now the console asked for one page and showed it as if it were the queue.
   *
   * `useInfiniteQuery` under the SAME key prefix the event stream invalidates (`['inbox']`), so a
   * frame arriving on page three refetches all three pages rather than resetting to the first —
   * losing somebody's place in a queue they are working is its own small betrayal.
   */
  const inbox = useInfiniteQuery({
    queryKey: ['inbox', { view, tier, lang, q }],
    queryFn: ({ pageParam }) =>
      api.inbox({ view, tier, lang, q, ...(pageParam === null ? {} : { cursor: pageParam }) }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = useMemo(() => inbox.data?.pages.flatMap((p) => p.rows) ?? [], [inbox.data]);

  // Pick the first conversation once the list arrives, so the middle pane is never blank.
  useEffect(() => {
    if (!conversationId && rows.length > 0) {
      navigate(`/inbox/${rows[0]?.conversationId}${params.toString() ? `?${params}` : ''}`, { replace: true });
    }
  }, [conversationId, rows, navigate, params]);

  const activeIndex = rows.findIndex((r) => r.conversationId === conversationId);

  /**
   * The open thread survives falling out of the list it was opened from.
   *
   * Found by the e2e journey, not by clicking: reply to somebody from the **Unanswered** view and
   * the reply is exactly what removes them from it. The list refetches, `rows` no longer holds
   * that conversation, `selected` becomes undefined, and the middle pane you were typing in is
   * replaced by "Pick a conversation" — with the URL still pointing at the thread. You cannot see
   * your own message land, you cannot see its delivery tick, and the answer to "did that send?"
   * is a blank panel. The same thing happens on **Handed over** the moment a handoff is released.
   *
   * So the row is held for as long as its conversation is the one in the URL. The pane below says
   * when it has left the current filter, because a thread that is no longer in the list you are
   * working must not quietly look like one that is.
   */
  const [lastSeen, setLastSeen] = useState<Conversation | null>(null);
  const inList = rows[activeIndex];
  useEffect(() => {
    if (inList) setLastSeen(inList);
    else if (lastSeen && lastSeen.conversationId !== conversationId) setLastSeen(null);
  }, [inList, conversationId, lastSeen]);
  const selected = inList ?? (lastSeen?.conversationId === conversationId ? lastSeen : undefined);
  const droppedOut = Boolean(selected && !inList);

  // j/k through the list from anywhere on the page that is not a text field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key !== 'j' && e.key !== 'k') return;
      const delta = e.key === 'j' ? 1 : -1;
      const next = rows[Math.min(rows.length - 1, Math.max(0, activeIndex + delta))];
      if (next) {
        e.preventDefault();
        navigate(`/inbox/${next.conversationId}${params.toString() ? `?${params}` : ''}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, activeIndex, navigate, params]);

  const lead = useQuery({
    queryKey: ['lead', selected?.leadId],
    queryFn: () => api.lead(selected?.leadId as string),
    enabled: Boolean(selected?.leadId),
  });

  // No `refetchInterval`. The thread is refetched when `/api/v1/events` says a message or a
  // delivery status landed on this conversation — see src/lib/events.tsx. A poll asked four times
  // a second-and-a-half whether anything had happened and still took up to four seconds to show
  // a customer's message; the stream says it once, on commit.
  const messages = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: () => api.messages(conversationId as string),
    enabled: Boolean(conversationId),
  });

  const [explainTurn, setExplainTurn] = useState<string | null>(null);
  const explain = useQuery({
    queryKey: ['explain', explainTurn],
    queryFn: () => api.explain(explainTurn as string),
    enabled: Boolean(explainTurn),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    void queryClient.invalidateQueries({ queryKey: ['lead', selected?.leadId] });
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
    void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
  };

  const send = useMutation({
    mutationFn: (text: string) => api.sendMessage(selected?.leadId as string, text),
    onSuccess: () => {
      toast('Sent', { tone: 'ok' });
      invalidate();
    },
    // There is no `templateRequired` flag on the wire: `ErrorResponse` is `{error:{code,message}}`
    // and widening it was not this task's schema to widen. The API's own sentence says which
    // refusal this is — closed window, opted out, daily ceiling — so it is shown verbatim.
    onError: (err) => toastError('send that reply', err),
  });

  /**
   * Taking the thread over IS claiming its handoff.
   *
   * There is no `POST /leads/:id/takeover`: `POST /handoffs/:id/claim` is what writes
   * `handoff.takeovers` and mutes the agent for twenty minutes, and releasing gives it back.
   * A lead with no open handoff therefore cannot be taken over at all today — the button says so
   * rather than calling a route that does not exist.
   */
  const openHandoffId = lead.data?.openHandoffId ?? selected?.handoff?.id ?? null;
  const held = lead.data?.takeover ?? selected?.takeover ?? null;
  const mayWrite = canWrite(user.role);
  const writeGate = roleGate(mayWrite, 'Replying and taking over need a role of rep or above', user.role);

  const takeover = useMutation({
    mutationFn: () =>
      held ? api.releaseHandoff(openHandoffId as string) : api.claimHandoff(openHandoffId as string),
    onSuccess: () => {
      toast(held ? 'Released — the agent answers this thread again' : 'Claimed — the agent is muted on this thread', {
        tone: 'ok',
        body: held ? undefined : 'Close it with an outcome from the Handoffs queue when you are done.',
      });
      invalidate();
    },
    onError: (err) => toastError('change who holds this thread', err),
  });

  return (
    <div className="grid h-full min-h-0 grid-cols-1 lg:grid-cols-[22rem_minmax(0,1fr)_24rem]">
      {/* Pane 1 — conversations */}
      <aside className="flex min-h-0 flex-col border-r border-line bg-surface">
        <div className="flex flex-col gap-2 border-b border-line p-3">
          <input
            type="search"
            value={q}
            onChange={(e) => setParam('q', e.target.value)}
            placeholder="Search names and messages…"
            aria-label="Search conversations"
            className="h-8 rounded-[var(--h1-radius)] border border-line-strong bg-bg px-2.5 text-sm text-fg"
          />
          <div role="tablist" aria-label="Inbox views" className="flex gap-1">
            {VIEWS.map((v) => (
              <button
                key={v.key}
                role="tab"
                aria-selected={view === v.key}
                onClick={() => setParam('view', v.key)}
                className={clsx(
                  'rounded-full px-2.5 py-1 text-[11px] font-medium',
                  view === v.key ? 'bg-brand-soft text-brand-soft-fg' : 'text-fg-muted hover:bg-surface-2',
                )}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <select
              value={tier}
              onChange={(e) => setParam('tier', e.target.value)}
              aria-label="Filter by tier"
              className="h-7 flex-1 rounded border border-line-strong bg-bg px-1.5 text-[11px] text-fg"
            >
              <option value="all">All tiers</option>
              <option value="hot">Hot</option>
              <option value="warm">Warm</option>
              <option value="cold">Cold</option>
            </select>
            <select
              value={lang}
              onChange={(e) => setParam('lang', e.target.value)}
              aria-label="Filter by language"
              className="h-7 flex-1 rounded border border-line-strong bg-bg px-1.5 text-[11px] text-fg"
            >
              <option value="all">All languages</option>
              <option value="en">English</option>
              <option value="hi">हिन्दी</option>
              <option value="hinglish">Hinglish</option>
              {/* `convo.conversations.language` is null early in most threads and the API answers
                  "unknown" for it. Without this option those rows are unreachable by filter. */}
              <option value="unknown">Not yet known</option>
            </select>
          </div>
        </div>

        <div className="h1-scroll min-h-0 flex-1 overflow-y-auto">
          {inbox.isError ? (
            <ErrorState error={inbox.error} context="load the inbox" onRetry={() => void inbox.refetch()} />
          ) : inbox.isLoading ? (
            <p className="p-4 text-sm text-fg-muted">Loading conversations…</p>
          ) : rows.length === 0 ? (
            <EmptyState title="No conversations match" body="Clear a filter or widen the search." />
          ) : (
            <>
            {/*
              Named, because `aside li` also matches the lead 360's own lists — its score factors,
              its promises, its notes. A count taken over both panes is a count of nothing.
            */}
            <ul data-testid="conversation-list">
              {rows.map((c) => {
                const phone = phoneFor(user.role, { phone: c.phone, contactHash: c.contactHash });
                const active = c.conversationId === conversationId;
                return (
                  <li key={c.conversationId}>
                    <button
                      type="button"
                      onClick={() => navigate(`/inbox/${c.conversationId}${params.toString() ? `?${params}` : ''}`)}
                      aria-current={active ? 'true' : undefined}
                      className={clsx(
                        'flex w-full flex-col gap-1 border-b border-line px-3 py-2.5 text-left',
                        active ? 'h1-row-active' : 'hover:bg-surface-2',
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{c.displayName}</span>
                        {c.unanswered && (
                          <span
                            className={clsx(
                              'shrink-0 text-[11px] font-semibold',
                              c.unansweredMs > 30 * 60_000 ? 'text-danger' : 'text-warn',
                            )}
                            title="Time since the last inbound message with no reply after it"
                          >
                            {formatDuration(c.unansweredMs)}
                          </span>
                        )}
                      </span>
                      <span className="truncate text-xs opacity-80">{c.lastMessagePreview}</span>
                      <span className="flex flex-wrap items-center gap-1">
                        <TierPill tier={c.tier} />
                        <Pill tone="neutral">{LANG_LABEL[c.lang] ?? c.lang}</Pill>
                        {c.handoff && (
                          <Pill tone={c.handoff.status === 'open' ? 'danger' : 'warn'}>
                            handoff {c.handoff.status}
                          </Pill>
                        )}
                        {c.takeover && (
                          <Pill tone="brand" title={`${c.takeover.by} has this thread`}>
                            {personLabel(c.takeover.by)} has it
                          </Pill>
                        )}
                        <span className={clsx('ml-auto text-[10px]', phone.redacted && 'font-mono')}>{phone.text}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            {/*
              How much of the queue this is. `j`/`k` walk what has been loaded; the button loads
              the next fifty. A list that ends silently at fifty is a list somebody trusts.
            */}
            <div className="flex items-center gap-2 px-3 py-2.5">
              <p className="text-[11px] text-fg-subtle">
                {rows.length} conversation{rows.length === 1 ? '' : 's'}
                {inbox.hasNextPage ? ' — more below' : ' — that is all of them'}
              </p>
              {inbox.hasNextPage && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={inbox.isFetchingNextPage}
                  onClick={() => void inbox.fetchNextPage()}
                >
                  {inbox.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </Button>
              )}
            </div>
            </>
          )}
        </div>
      </aside>

      {/* Pane 2 — thread */}
      <section className="flex min-h-0 flex-col bg-bg">
        {!selected ? (
          <EmptyState title="Pick a conversation" body="Use j and k to move through the list." />
        ) : (
          <>
            <header className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-4 py-2.5">
              <h2 className="text-sm font-semibold text-fg">{selected.displayName}</h2>
              {/* The board says "Handed over"; this said `handed_over` for the same lead, because
                  only `GET /pipeline` carries the API's stage labels. */}
              <Pill tone="neutral">{stageLabel(selected.stage)}</Pill>
              {droppedOut && (
                <span
                  className="rounded-full border border-dashed border-line-strong px-2 py-0.5 text-[11px] text-fg-subtle"
                  title="Still open here because it is the thread in the address bar. Clear the filter, or pick another conversation, to put the list and this pane back in step."
                >
                  no longer in this view
                </span>
              )}
              {held && (
                <Pill tone="brand" dot title={`${held.by} has this thread`}>
                  {personLabel(held.by)} has the thread until {formatTime(held.until)}
                </Pill>
              )}
              <div className="ml-auto flex items-center gap-2">
                {openHandoffId === null ? (
                  <span
                    className="rounded-[var(--h1-radius)] border border-dashed border-line-strong px-2 py-1 text-[11px] text-fg-subtle"
                    title="Claiming a handoff is what mutes the agent and records the take-over. Taking over a thread with no handoff open is not built — the service function exists, the mute rules for stealing a live thread are a product decision nobody has made."
                  >
                    No open handoff — take-over not available yet
                  </span>
                ) : (
                  <Button
                    size="sm"
                    variant={held ? 'secondary' : 'primary'}
                    disabled={takeover.isPending || !mayWrite}
                    onClick={() => takeover.mutate()}
                    title={
                      writeGate ??
                      (held
                        ? 'Release the handoff: the agent starts answering this thread again'
                        : 'Claim the handoff: the agent is muted on this thread for 20 minutes and the claim is recorded against your name')
                    }
                  >
                    {takeover.isPending ? 'Working…' : held ? 'Release to the agent' : 'Take over (claim handoff)'}
                  </Button>
                )}
              </div>
            </header>

            <div className="h1-scroll flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-4">
              {messages.isError ? (
                <ErrorState error={messages.error} context="load this thread" onRetry={() => void messages.refetch()} />
              ) : messages.isLoading ? (
                <p className="text-sm text-fg-muted">Loading thread…</p>
              ) : (
                (messages.data ?? []).map((m) => (
                  <Bubble
                    key={m.id}
                    message={m}
                    agentName={agentName}
                    onExplain={canSeeExplain(user.role) ? (turnId) => setExplainTurn(turnId) : undefined}
                  />
                ))
              )}
            </div>

            <Composer
              lead={lead.data ?? null}
              // Whether we KNOW the window's state, which is not the same as it being shut.
              windowKnown={lead.isSuccess}
              sending={send.isPending}
              onSend={(text) => send.mutate(text)}
              canSteer={canSteer(user.role)}
              canSend={mayWrite}
              sendGate={writeGate}
            />
          </>
        )}
      </section>

      {/* Pane 3 — lead 360 */}
      <aside className="h1-scroll hidden min-h-0 overflow-y-auto border-l border-line bg-surface lg:block">
        {lead.isError ? (
          <ErrorState error={lead.error} context="load this lead" onRetry={() => void lead.refetch()} />
        ) : lead.data ? (
          <Lead360 lead={lead.data} />
        ) : !selected?.leadId ? (
          // Nothing is selected, so the query is `enabled: false` and will never resolve. Saying
          // "Loading lead…" here was a spinner that ran for ever on an empty inbox — the state
          // people see first, and the one that reads as a broken page rather than an empty one.
          <p className="p-4 text-sm text-fg-muted">
            No lead selected. Pick a conversation and everything known about the person lands here.
          </p>
        ) : (
          <p className="p-4 text-sm text-fg-muted">Loading lead…</p>
        )}
      </aside>

      <Drawer
        open={Boolean(explainTurn)}
        onOpenChange={(open) => !open && setExplainTurn(null)}
        title={`Why ${agentName} said that`}
        description={explainTurn ? `Turn ${explainTurn}` : undefined}
        size="lg"
      >
        {explain.isLoading && <p className="text-sm text-fg-muted">Loading…</p>}
        {explain.data && <ExplainPanel explain={explain.data} />}
      </Drawer>
    </div>
  );
}

function Composer({
  lead,
  windowKnown,
  sending,
  onSend,
  canSteer: maySteer,
  canSend,
  sendGate,
}: {
  lead: Lead | null;
  /** False while `GET /leads/:id` is in flight, and after it has failed. */
  windowKnown: boolean;
  sending: boolean;
  onSend: (text: string) => void;
  canSteer: boolean;
  canSend: boolean;
  sendGate: string | undefined;
}): ReactNode {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement>(null);

  // `r` focuses the composer, the way a queue tool should.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const t = e.target as HTMLElement | null;
      if (t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable) return;
      if (e.key === 'r' && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * "Window closed" is a claim about Meta's 24-hour rule, and it must not be made before the
   * answer is in. `lead` is null while `GET /leads/:id` is in flight and null again after it
   * fails, so the banner asserted the window was shut — in warning colours — on the first frame
   * of every thread, and permanently on a lead that failed to load.
   */
  const open = windowKnown && (lead?.windowMsLeft ?? 0) > 0;
  const armed = open && canSend;

  return (
    <div className="shrink-0 border-t border-line bg-surface p-3">
      <div
        className={clsx(
          'mb-2 rounded-[var(--h1-radius)] px-2.5 py-1.5 text-[11px]',
          !windowKnown ? 'bg-neutral-soft text-neutral-soft-fg' : open ? 'bg-ok-soft text-ok-soft-fg' : 'bg-warn-soft text-warn-soft-fg',
        )}
        role="status"
      >
        {!windowKnown
          ? 'Checking whether the 24-hour service window is still open…'
          : open
            ? `Free-form replies allowed for ${formatDuration(lead?.windowMsLeft ?? 0)} — the 24-hour service window is open.`
            : 'Window closed — Meta will reject a free-form message. An approved template is the only way through.'}
      </div>
      {sendGate && (
        <p className="mb-2 rounded-[var(--h1-radius)] bg-neutral-soft px-2.5 py-1.5 text-[11px] text-neutral-soft-fg" role="status">
          {sendGate}
        </p>
      )}

      <div className="flex items-end gap-2">
        <textarea
          ref={ref}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={!armed || sending}
          rows={2}
          placeholder={
            armed
              ? 'Type a reply… (r to focus, ⌘Enter to send)'
              : open
                ? 'Your role cannot reply'
                : windowKnown
                  ? 'Pick a template instead'
                  : 'Checking the window…'
          }
          aria-label="Reply to the customer"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && text.trim()) {
              onSend(text.trim());
              setText('');
            }
          }}
          className="h1-scroll min-h-16 flex-1 resize-y rounded-[var(--h1-radius)] border border-line-strong bg-bg px-3 py-2 text-sm text-fg disabled:opacity-60"
        />
        <div className="flex flex-col gap-1.5">
          <Button
            disabled={!armed || sending || !text.trim()}
            title={sendGate}
            onClick={() => {
              onSend(text.trim());
              setText('');
            }}
          >
            {sending ? 'Sending…' : 'Send'}
          </Button>
          {windowKnown && !open && (
            <Button variant="secondary" size="sm" disabled title="Not built yet — the template picker lands with the Growth Studio in Phase 2">
              Templates — not built yet
            </Button>
          )}
        </div>
      </div>

      {/*
        Steering is DISABLED, visibly, rather than hidden or faked.

        `POST /leads/:id/steer` does not exist, and the reason it does not is the point: a steer
        note is only worth anything if `turn/packet.ts` reads it before the next reply, and it does
        not. Shipping the box would be a knob that changes nothing — the worst kind, because the
        person who typed into it believes the agent was told.
      */}
      {maySteer && (
        <div className="mt-2 rounded-[var(--h1-radius)] border border-dashed border-line-strong px-2.5 py-2">
          <p className="text-[11px] font-medium text-fg-muted">
            Steer the next turn — <span className="text-fg-subtle">not built yet</span>
          </p>
          <textarea
            value=""
            readOnly
            disabled
            rows={2}
            placeholder="e.g. do not quote a delivery date; offer the studio visit instead"
            aria-label="Instruction for the agent's next turn (disabled — the runtime does not read it yet)"
            className="mt-1.5 w-full cursor-not-allowed resize-none rounded-[var(--h1-radius)] border border-line bg-surface-2 px-3 py-2 text-sm text-fg-subtle"
          />
          {/* The reason, without our schema in it: `convo.steer_notes` is the name of one of our
              tables, and this box is read by the client's own managers and owner. What is theirs
              to know is that a note here would not reach the agent. */}
          <p className="mt-1 text-[11px] text-fg-subtle">
            Nothing the agent reads before its next reply looks at this box yet, so a note saved here would change
            nothing about what the customer is told next.
          </p>
        </div>
      )}
    </div>
  );
}

function Lead360({ lead }: { lead: Lead }): ReactNode {
  const user = useUser();
  const phone = phoneFor(user.role, lead.contact);
  const hits = lead.score.factors.filter((f) => f.hit);
  const misses = lead.score.factors.filter((f) => !f.hit);

  return (
    <div className="flex flex-col gap-5 p-4">
      <section>
        <h2 className="text-base font-semibold text-fg">{lead.contact.displayName}</h2>
        <p className={clsx('mt-0.5 text-xs', phone.redacted ? 'font-mono text-fg-subtle' : 'text-fg-muted')}>
          {phone.text}
          {phone.redacted && ' · number hidden for your role'}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <TierPill tier={lead.score.tier} score={lead.score.value} />
          <Pill tone="neutral">{stageLabel(lead.stage)}</Pill>
          {/* `routes/contacts.ts` defaults `customerType` to "unknown", so this pill read a literal
              "unknown" on every contact nobody had classified. */}
          <Pill tone="neutral">{customerTypeLabel(lead.contact.customerType)}</Pill>
          <Pill tone="neutral">{LANG_LABEL[lead.contact.lang] ?? lead.contact.lang}</Pill>
          {lead.contact.city && <Pill tone="neutral">{lead.contact.city}</Pill>}
          {lead.contact.optedOut && <Pill tone="danger">opted out</Pill>}
          {lead.tags.map((t) => (
            <Pill key={t} tone="info">
              #{t}
            </Pill>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
          Score {lead.score.value} · {lead.score.version}
        </h3>
        <ul className="flex flex-col gap-1">
          {hits.map((f) => (
            <li key={f.key} className="rounded-md bg-ok-soft/60 px-2 py-1.5">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-medium text-fg">{f.label}</span>
                <span className="font-mono text-[11px] text-ok">+{f.points}</span>
              </div>
              {f.evidence && <p className="mt-0.5 text-[11px] italic text-fg-muted">“{f.evidence}”</p>}
            </li>
          ))}
          {misses.length > 0 && (
            <li className="mt-1 text-[11px] text-fg-subtle">
              Not yet: {misses.map((f) => f.label.toLowerCase()).join(' · ')}
            </li>
          )}
        </ul>
      </section>

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Facts</h3>
        {lead.facts.length === 0 ? (
          <p className="text-xs text-fg-muted">Nothing captured yet.</p>
        ) : (
          <dl className="flex flex-col gap-1.5">
            {lead.facts.map((f) => (
              <div key={`${f.key}-${f.at}`} className={clsx('rounded-md border border-line p-2', f.superseded && 'opacity-50')}>
                <div className="flex items-baseline justify-between gap-2">
                  {/* The API humanizes score-factor and handoff-brief labels but not fact keys, so
                      this printed `asked_for_call_or_visit` at a sales manager. The raw key stays
                      in the title, because it is what somebody greps the ledger for. */}
                  <dt className="text-[11px] font-medium text-fg-subtle" title={f.key}>{humanize(f.key)}</dt>
                  <Pill tone={f.source === 'customer' ? 'ok' : f.source === 'rep' ? 'info' : 'neutral'}>{f.source}</Pill>
                </div>
                <dd className="mt-0.5 text-sm text-fg">{f.value}</dd>
                {f.quote && <p className="mt-0.5 text-[11px] italic text-fg-muted">“{f.quote}”</p>}
                <p className="mt-0.5 text-[10px] text-fg-subtle" title={formatDateTime(f.at)}>
                  {formatAgo(f.at)}
                </p>
              </div>
            ))}
          </dl>
        )}
      </section>

      {lead.promises.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">
            Promises owed
          </h3>
          <ul className="flex flex-col gap-1.5">
            {lead.promises.map((p) => (
              <li
                key={p.id}
                className={clsx(
                  'rounded-md border p-2',
                  p.overdue ? 'border-danger bg-danger-soft/60' : 'border-line',
                )}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[11px] font-medium text-fg-subtle" title={p.kind}>{humanize(p.kind)}</span>
                  {/*
                    `GET /leads/:id` returns every OPEN promise, not only the overdue ones, so
                    `dueAt` is usually in the future here — which `formatAgo` used to render as
                    "just now". See the note on `formatAgo`.
                  */}
                  <Pill tone={p.overdue ? 'danger' : 'warn'} title={formatDateTime(p.dueAt)}>
                    {p.overdue ? 'overdue' : 'due'} {formatAgo(p.dueAt)}
                  </Pill>
                </div>
                <p className="mt-0.5 text-xs text-fg">{p.text}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {lead.notes.length > 0 && (
        <section>
          <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Notes</h3>
          <ul className="flex flex-col gap-1.5">
            {lead.notes.map((n) => (
              <li key={n.id} className="rounded-md bg-surface-2 p-2">
                <p className="text-xs text-fg">{n.body}</p>
                <p className="mt-0.5 text-[10px] text-fg-subtle" title={n.author}>
                  {personLabel(n.author)} · {formatAgo(n.at)}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h3 className="mb-1.5 text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">Timeline</h3>
        <Timeline entries={lead.timeline.slice(0, 20)} />
      </section>
    </div>
  );
}
